import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { examAPI } from '../lib/api';
import { DISCORD_INVITE_URL } from '../config/discord';
import { 
  User, Target, Trophy, BookOpen, Star, 
  TrendingUp, Clock, Award, ChevronRight, Bell,
  Link2, Unlink, Loader2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [exams, setExams] = useState([]);
  const [selectedExams, setSelectedExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examUpdates, setExamUpdates] = useState([]);
  const [userStats, setUserStats] = useState({
    total_tests: 0,
    accuracy: 0,
    total_questions_attempted: 0,
    total_time_spent: 0
  });
  
  // Discord integration state
  const [discordStatus, setDiscordStatus] = useState({
    discord_enabled: false,
    connected: false,
    discord_username: null,
    discord_id: null
  });
  const [discordLoading, setDiscordLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadDiscordStatus();
    
    // Handle Discord OAuth callback
    const discordCode = searchParams.get('code');
    if (discordCode) {
      handleDiscordCallback(discordCode);
      // Clean up URL
      window.history.replaceState({}, '', '/profile');
    }
  }, []);
  
  const loadDiscordStatus = async () => {
    try {
      const res = await api.get('/discord/status');
      setDiscordStatus(res.data);
    } catch (error) {
      // Discord status not available - silent fail
    }
  };
  
  const handleDiscordConnect = async () => {
    setDiscordLoading(true);
    try {
      const res = await api.get('/discord/auth-url');
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Discord integration not configured');
      setDiscordLoading(false);
    }
  };
  
  const handleDiscordCallback = async (code) => {
    setDiscordLoading(true);
    try {
      const res = await api.post('/discord/callback', { code });
      toast.success(res.data.message || 'Discord connected!');
      setDiscordStatus({
        ...discordStatus,
        connected: true,
        discord_id: res.data.discord_id,
        discord_username: res.data.discord_username
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Discord');
    } finally {
      setDiscordLoading(false);
    }
  };
  
  const handleDiscordUnlink = async () => {
    if (!window.confirm('Unlink your Discord account? You will lose access to premium Discord channels.')) return;
    
    setDiscordLoading(true);
    try {
      await api.post('/discord/unlink');
      toast.success('Discord unlinked');
      setDiscordStatus({
        ...discordStatus,
        connected: false,
        discord_id: null,
        discord_username: null
      });
    } catch (error) {
      toast.error('Failed to unlink Discord');
    } finally {
      setDiscordLoading(false);
    }
  };
  
  const syncDiscordRole = async () => {
    setDiscordLoading(true);
    try {
      await api.post('/discord/sync-role');
      toast.success('Discord role synced!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync role');
    } finally {
      setDiscordLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const response = await examAPI.getAll();
      setExams(response.data);
      
      // Load user stats
      try {
        const statsRes = await api.get('/user/stats');
        setUserStats(statsRes.data);
      } catch (error) {
        // Stats unavailable - silent fail
      }
      
      // Load user's selected exams from backend
      try {
        const profileRes = await api.get('/profile/updates');
        setExamUpdates(profileRes.data || []);
      } catch (error) {
        // No exam updates available
        setExamUpdates([]);
      }
      
      // Load user's selected exams from localStorage or user profile
      const saved = localStorage.getItem('selectedExams');
      if (saved) {
        setSelectedExams(JSON.parse(saved));
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleExamSelection = (examId) => {
    const newSelection = selectedExams.includes(examId)
      ? selectedExams.filter(id => id !== examId)
      : [...selectedExams, examId];
    
    setSelectedExams(newSelection);
    localStorage.setItem('selectedExams', JSON.stringify(newSelection));
    toast.success('Exam preferences updated');
  };

  const selectedExamDetails = exams.filter(e => selectedExams.includes(e.id));
  const relevantUpdates = examUpdates.filter(u => 
    selectedExamDetails.some(e => e.name === u.exam)
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Profile Header */}
        <Card className="p-8 bg-gradient-to-r from-primary to-primary/80 text-white rounded-3xl mb-8">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
              <User size={40} />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-1">{user?.name || 'Student'}</h1>
              <p className="text-white/80">{user?.email}</p>
              <div className="flex gap-4 mt-3">
                <div className="flex items-center gap-2">
                  <Trophy size={18} />
                  <span>{user?.points || 0} points</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star size={18} />
                  <span>{user?.streak || 0} day streak</span>
                </div>
                {user?.premium && (
                  <Badge className="bg-amber-500 text-white">PRO</Badge>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Exam Updates */}
        {relevantUpdates.length > 0 && (
          <Card className="p-6 bg-card rounded-2xl mb-8 border border-border">
            <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
              <Bell className="text-primary" size={20} />
              Exam Updates
            </h2>
            <div className="space-y-3">
              {relevantUpdates.map((update, idx) => (
                <div key={idx} className="p-4 bg-muted rounded-xl">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-foreground">{update.exam}</span>
                    <span className="text-xs text-muted-foreground">{update.date}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{update.update}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Select Exams */}
        <Card className="p-6 bg-card rounded-2xl mb-8 border border-border">
          <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <Target className="text-primary" size={20} />
            Exams I'm Preparing For
          </h2>
          
          {selectedExamDetails.length > 0 && (
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-3">Currently preparing for:</p>
              <div className="flex flex-wrap gap-2">
                {selectedExamDetails.map(exam => (
                  <Badge 
                    key={exam.id} 
                    className="bg-primary/10 text-primary px-3 py-1 text-sm cursor-pointer hover:bg-primary/20"
                    onClick={() => toggleExamSelection(exam.id)}
                  >
                    {exam.icon} {exam.name} ✕
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          <p className="text-sm text-muted-foreground mb-3">Select exams to get personalized updates:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {exams.filter(e => !selectedExams.includes(e.id) && e.is_available).map(exam => (
              <button
                key={exam.id}
                onClick={() => toggleExamSelection(exam.id)}
                className="p-3 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all text-left"
              >
                <span className="text-lg mr-2">{exam.icon}</span>
                <span className="text-sm font-medium text-foreground">{exam.name}</span>
              </button>
            ))}
            {exams.filter(e => !selectedExams.includes(e.id) && !e.is_available).slice(0, 6).map(exam => (
              <div
                key={exam.id}
                className="p-3 rounded-xl border border-dashed border-border opacity-60 text-left cursor-not-allowed"
              >
                <span className="text-lg mr-2">🔒</span>
                <span className="text-sm font-medium text-muted-foreground">{exam.name}</span>
                <span className="block text-xs text-amber-500 mt-1">Coming Soon</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-card rounded-2xl border border-border text-center">
            <BookOpen className="text-primary mx-auto mb-2" size={24} />
            <p className="text-2xl font-bold text-foreground">{userStats.total_tests || 0}</p>
            <p className="text-xs text-muted-foreground">Tests Taken</p>
          </Card>
          <Card className="p-4 bg-card rounded-2xl border border-border text-center">
            <Target className="text-green-500 mx-auto mb-2" size={24} />
            <p className="text-2xl font-bold text-foreground">{userStats.accuracy || 0}%</p>
            <p className="text-xs text-muted-foreground">Avg Score</p>
          </Card>
          <Card className="p-4 bg-card rounded-2xl border border-border text-center">
            <Clock className="text-amber-500 mx-auto mb-2" size={24} />
            <p className="text-2xl font-bold text-foreground">{Math.round((userStats.total_time_spent || 0) / 3600)}h</p>
            <p className="text-xs text-muted-foreground">Study Time</p>
          </Card>
          <Card className="p-4 bg-card rounded-2xl border border-border text-center">
            <TrendingUp className="text-purple-500 mx-auto mb-2" size={24} />
            <p className="text-2xl font-bold text-foreground">{userStats.total_questions_attempted || 0}</p>
            <p className="text-xs text-muted-foreground">Questions</p>
          </Card>
        </div>

        {/* Subscription Status */}
        <Card className="p-6 bg-card rounded-2xl border border-border mb-6">
          <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <Award className="text-amber-500" size={20} />
            Subscription
          </h2>
          {user?.premium ? (
            <div className="p-4 bg-amber-500/10 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-foreground">PRO Member</p>
                  <p className="text-sm text-muted-foreground">All features unlocked</p>
                </div>
                <Badge className="bg-amber-500 text-white">ACTIVE</Badge>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-muted rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-foreground">Free Plan</p>
                  <p className="text-sm text-muted-foreground">Upgrade for full access</p>
                </div>
                <Button onClick={() => navigate('/subscription')} className="bg-primary text-white">
                  Upgrade
                  <ChevronRight size={18} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Discord Integration */}
        <Card className="p-6 bg-card rounded-2xl border border-border">
          <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Discord Integration
          </h2>
          
          {discordStatus.connected ? (
            <div className="space-y-4">
              <div className="p-4 bg-[#5865F2]/10 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#5865F2] rounded-full flex items-center justify-center text-white font-bold">
                      {discordStatus.discord_username?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{discordStatus.discord_username}</p>
                      <p className="text-xs text-muted-foreground">Discord Connected</p>
                    </div>
                  </div>
                  <Badge className="bg-[#5865F2] text-white">LINKED</Badge>
                </div>
              </div>
              
              <div className="flex gap-2 flex-wrap">
                {user?.premium && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={syncDiscordRole}
                    disabled={discordLoading}
                    className="text-[#5865F2] border-[#5865F2]/50"
                  >
                    {discordLoading ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
                    Sync Premium Role
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(DISCORD_INVITE_URL, '_blank')}
                  className="text-muted-foreground"
                >
                  <ExternalLink size={14} className="mr-1" />
                  Join Server
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDiscordUnlink}
                  disabled={discordLoading}
                  className="text-red-500 hover:bg-red-500/10"
                >
                  {discordLoading ? <Loader2 className="animate-spin mr-1" size={14} /> : <Unlink size={14} className="mr-1" />}
                  Unlink
                </Button>
              </div>
              
              {user?.premium && (
                <p className="text-xs text-muted-foreground">
                  As a PRO member, you have access to exclusive Discord channels.
                </p>
              )}
            </div>
          ) : (
            <div className="p-4 bg-muted rounded-xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-foreground">Connect Discord</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Join our community! Premium members get exclusive Discord channels and roles.
                  </p>
                  <ul className="text-xs text-muted-foreground mt-2 space-y-1">
                    <li>• Get the Member role automatically</li>
                    <li>• Premium users get exclusive Premium channels</li>
                    <li>• Connect with other aspirants</li>
                  </ul>
                </div>
                <Button
                  onClick={handleDiscordConnect}
                  disabled={discordLoading || !discordStatus.discord_enabled}
                  className="bg-[#5865F2] hover:bg-[#4752C4] text-white flex-shrink-0"
                >
                  {discordLoading ? (
                    <Loader2 className="animate-spin mr-1" size={16} />
                  ) : (
                    <Link2 size={16} className="mr-1" />
                  )}
                  Connect
                </Button>
              </div>
              {!discordStatus.discord_enabled && (
                <p className="text-xs text-amber-500 mt-2">Discord integration coming soon.</p>
              )}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default Profile;
